import { Permissions, usePermissions } from '@app/src/auth/permissions'
import type { SortableHeaderProps } from '@app/src/components/Table'
import { TableCellWidth } from '@app/src/components/Table'
import TableHeaderCell from '@app/src/components/Table/Cells/TableHeaderCell'
import type { Inquiry } from '@app/src/types/resourceExplorer'
import type { CheckboxProps } from '@mui/material'
import { TableRow } from '@mui/material'
import type { ReactNode } from 'react'
import React from 'react'
import { useIntl } from 'react-intl'

interface AccessorInquiryHeaderProps extends SortableHeaderProps {
  isHeaderChecked?: boolean
  setSelectedInquiriesIds?: React.Dispatch<React.SetStateAction<Array<number>>>
  inquiriesInPage?: Inquiry[]
  additionalColumn?: ReactNode
  showTargetProductColumn?: boolean
}

function AccessorInquiryHeader({
  toggleSorting,
  activeSorting,
  inquiriesInPage,
  isHeaderChecked,
  setSelectedInquiriesIds,
  additionalColumn,
  showTargetProductColumn,
}: AccessorInquiryHeaderProps) {
  const { formatMessage } = useIntl()
  const { hasPermission } = usePermissions()
  const verificationEnabled = hasPermission(Permissions.WRITE_VERIFICATION)

  const shouldShowCheckboxRow = setSelectedInquiriesIds && inquiriesInPage // Inquiries table is shown on the insights modal, so we need to support not having this

  const handleCheckboxChange: CheckboxProps['onChange'] = (_e, checked) => {
    if (!shouldShowCheckboxRow) return
    if (checked) {
      setSelectedInquiriesIds(prev => Array.from(new Set([...prev, ...inquiriesInPage.map(request => request.id)])))
    } else {
      setSelectedInquiriesIds([])
    }
  }

  return (
    <TableRow>
      {shouldShowCheckboxRow && (
        <TableHeaderCell
          label=""
          checkbox={{ checked: isHeaderChecked, onChange: handleCheckboxChange, color: 'primary' }}
          minWidth={TableCellWidth.EMPTY}
        />
      )}
      <TableHeaderCell
        label={formatMessage({ id: 'schemas.request.requestStatus' })}
        guidance={formatMessage({ id: 'schemas.request.guidance.status' })}
        minWidth={TableCellWidth.EMPTY}
      />
      <TableHeaderCell
        label={formatMessage({ id: 'schemas.request.request' })}
        sorting={{ name: 'template.title', toggleSorting, activeSorting }}
        minWidth={TableCellWidth.EXTRA_LARGE}
      />
      {showTargetProductColumn && <TableHeaderCell label={formatMessage({ id: 'schemas.mappingNode.product' })} />}
      <TableHeaderCell
        label={formatMessage({ id: 'schemas.request.periodDisplayName' })}
        minWidth={TableCellWidth.SMALL}
      />
      <TableHeaderCell label={formatMessage({ id: 'schemas.request.target' })} />
      {additionalColumn}
      <TableHeaderCell
        label={formatMessage({ id: 'schemas.request.createdAt' })}
        sorting={{ name: 'createdAt', toggleSorting, activeSorting }}
      />
      <TableHeaderCell
        label={formatMessage({ id: 'schemas.request.deadline' })}
        sorting={{
          name: 'deadline',
          toggleSorting,
          activeSorting,
        }}
      />
      <TableHeaderCell label={formatMessage({ id: 'schemas.request.responseDate' })} />
      <TableHeaderCell label={formatMessage({ id: 'schemas.provider.contacts' })} />

      {verificationEnabled && (
        <>
          <TableHeaderCell
            label={formatMessage({ id: 'schemas.request.externalVerificationComment' })}
            guidance={formatMessage({ id: 'schemas.request.guidance.externalVerificationComment' })}
            maxRows={1}
          />
          <TableHeaderCell
            label={formatMessage({ id: 'schemas.request.responseInternalStatus' })}
            guidance={formatMessage({ id: 'schemas.request.guidance.internalVerificationStatus' })}
            maxRows={1}
          />
          <TableHeaderCell
            label={formatMessage({ id: 'schemas.request.internalVerificationComment' })}
            guidance={formatMessage({ id: 'schemas.request.guidance.internalVerificationComment' })}
            maxRows={1}
          />
        </>
      )}
    </TableRow>
  )
}

export default AccessorInquiryHeader
